/**
 * NCC: <ncc-gift-message> - the bag's "Add a gift message" box (snippets/ncc-gift-message.liquid). Saves the message
 * on the cart as the attribute "Gift message" while the customer types (debounced, an older request aborted), keeps
 * the character counter, and clears the attribute when the box is emptied. Loaded once per page outside the
 * re-rendered bag markup (a script that arrives by morph never runs); the textarea and the counter carry
 * data-skip-node-update, so a bag re-render never undoes what is being typed.
 */
const SAVE_DELAY = 500;

class NccGiftMessage extends HTMLElement {
  /** @type {AbortController | null} */
  #request = null;
  /** @type {ReturnType<typeof setTimeout> | undefined} */
  #timer;

  connectedCallback() {
    this.addEventListener('input', this.#onInput);
    this.#count();
  }

  disconnectedCallback() {
    this.removeEventListener('input', this.#onInput);
    clearTimeout(this.#timer);
  }

  get #text() {
    return /** @type {HTMLTextAreaElement | null} */ (this.querySelector('[data-ref="text"]'));
  }

  #onInput = (/** @type {Event} */ event) => {
    if (event.target !== this.#text) return;
    this.#count();
    clearTimeout(this.#timer);
    this.#timer = setTimeout(() => this.#save(), SAVE_DELAY);
  };

  #count() {
    const text = this.#text;
    const count = this.querySelector('[data-ref="count"]');
    if (!text || !count) return;
    count.textContent = `${text.value.length}/${this.dataset.max || text.maxLength}`;
  }

  async #save() {
    const text = this.#text;
    if (!text) return;
    this.#request?.abort();
    const request = new AbortController();
    this.#request = request;
    try {
      await fetch(`${window.Shopify?.routes?.root || '/'}cart/update.js`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ attributes: { 'Gift message': text.value.trim() } }),
        signal: request.signal,
      });
    } catch (error) {
      // an aborted save is replaced by a newer one; anything else leaves the value for the Checkout post
      if (!(error instanceof DOMException && error.name === 'AbortError')) console.warn('Gift message not saved', error);
    } finally {
      if (this.#request === request) this.#request = null;
    }
  }
}

if (!customElements.get('ncc-gift-message')) {
  customElements.define('ncc-gift-message', NccGiftMessage);
}

/**
 * NCC: <ncc-cart-extras> - the bag's "+ Delivery instructions" / "+ Gift message" row (snippets/ncc-cart-extras.liquid):
 * each toggle shows or hides its box under the row (aria-expanded + hidden) and puts the cursor in it when it opens.
 */
class NccCartExtras extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#onClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#onClick);
  }

  #onClick = (/** @type {MouseEvent} */ event) => {
    const toggle = /** @type {HTMLElement | null} */ (
      event.target instanceof Element ? event.target.closest('.ncc-cart-extras__toggle') : null
    );
    if (!toggle) return;
    const panel = document.getElementById(toggle.getAttribute('aria-controls') || '');
    if (!panel) return;
    const open = toggle.getAttribute('aria-expanded') !== 'true';
    toggle.setAttribute('aria-expanded', String(open));
    panel.hidden = !open;
    if (open) panel.querySelector('textarea')?.focus({ preventScroll: true });
  };
}

if (!customElements.get('ncc-cart-extras')) {
  customElements.define('ncc-cart-extras', NccCartExtras);
}
